import { useState, useEffect, useMemo, useCallback, memo } from 'react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Progress } from './ui/progress';
import { Alert, AlertDescription } from './ui/alert';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { 
  CheckCircle, 
  Circle, 
  AlertCircle, 
  GitBranch, 
  Users, 
  List, 
  FileText, 
  Workflow,
  ChevronRight,
  Target,
  TrendingUp,
  Package,
  Rocket,
  Info,
  Edit,
  X
} from 'lucide-react';
import type { Project } from '../types';
import { projectId } from '../utils/supabase/info';
import { ConfigItemDetailDialog } from './ConfigItemDetailDialog';
import { apiCache } from '../utils/apiCache';

// Typical ranges for guidance (not prescriptive targets)
const TYPICAL_RANGES = {
  spaces: { min: 1, max: 3, description: 'Most implementations use 1-3 Jira spaces' },
  teams: { min: 2, max: 6, description: 'Typical organizations have 2-6 teams' },
  workflows: { min: 2, max: 5, description: 'Most service desks use 2-5 workflows' },
  workTypes: { min: 3, max: 10, description: 'Common range is 3-10 work types' },
  requestTypes: { min: 5, max: 30, description: 'Service desks typically have 5-30 request types' },
  fieldsPerRequest: { min: 3, max: 8, description: 'Average 3-8 fields per request type' }
};

interface ConfigurationItem {
  id: string;
  name: string;
  description: string;
  status: 'complete' | 'partial' | 'incomplete';
  count: number;
  implementedCount?: number;
  qualityScore: number; // 0-100 based on completeness and quality
  implementationPercent?: number;
  icon: any;
  details: string[];
  recommendations: string[];
  typicalRange?: string;
}

interface CompanyConfigurationGuideProps {
  companyId: string;
  companyName: string;
  projects: Project[];
  totalUsers: number;
  accessToken: string;
  currentUser: any;
  isConsultant?: boolean;
  isSystemAdmin?: boolean;
  refreshKey?: number; // Add refresh trigger to reload data
  onNavigateToProjects?: () => void;
  onNavigateToTeams?: () => void;
  onNavigateToWorkflows?: () => void;
  onNavigateToWorkTypes?: () => void;
  onNavigateToRequests?: () => void;
  onNavigateToFields?: () => void;
}

// Memoized component to prevent unnecessary re-renders
export const CompanyConfigurationGuide = memo(function CompanyConfigurationGuide({
  companyId,
  companyName,
  projects,
  totalUsers,
  accessToken,
  currentUser,
  isConsultant = false,
  isSystemAdmin = false,
  refreshKey = 0,
  onNavigateToProjects,
  onNavigateToTeams,
  onNavigateToWorkflows,
  onNavigateToWorkTypes,
  onNavigateToRequests,
  onNavigateToFields
}: CompanyConfigurationGuideProps) {
  const [expandedSection, setExpandedSection] = useState<string | null>(null);
  const [showGuidanceModal, setShowGuidanceModal] = useState(false);
  const [showDiscoveryOverrideDialog, setShowDiscoveryOverrideDialog] = useState(false);
  const [showImplementationOverrideDialog, setShowImplementationOverrideDialog] = useState(false);
  const [discoveryOverride, setDiscoveryOverride] = useState<number | null>(null);
  const [implementationOverride, setImplementationOverride] = useState<number | null>(null);
  const [tempDiscoveryValue, setTempDiscoveryValue] = useState<string>('');
  const [tempImplementationValue, setTempImplementationValue] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [defaultConfigItems, setDefaultConfigItems] = useState<any[]>([]);
  const [configItemsLoading, setConfigItemsLoading] = useState(true);
  const [configItemDetails, setConfigItemDetails] = useState<Record<string, any>>({});
  const [selectedConfigItem, setSelectedConfigItem] = useState<any | null>(null);
  const [showConfigItemDialog, setShowConfigItemDialog] = useState(false);
  const [localRefreshKey, setLocalRefreshKey] = useState(0);

  // Can override if consultant or system admin
  const canOverride = isConsultant || isSystemAdmin;

  // Load existing overrides and configuration items from backend (with caching)
  useEffect(() => {
    const loadData = async () => {
      setConfigItemsLoading(true);
      try {
        // Use cached requests to prevent duplicate API calls
        const [companyData, configData, detailsData] = await Promise.all([
          // Load company data (includes overrides)
          apiCache.get(`company-data-${companyId}`, async () => {
            const response = await fetch(
              `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/company-data/${companyId}`,
              {
                headers: {
                  'Authorization': `Bearer ${accessToken}`,
                },
              }
            );
            if (response.ok) {
              return response.json();
            }
            return null;
          }),
          
          // Load configuration items WITH overrides applied
          apiCache.get(`config-items-${companyId}`, async () => {
            const response = await fetch(
              `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/config/company/${companyId}`,
              {
                headers: {
                  'Authorization': `Bearer ${accessToken}`,
                },
              }
            );
            if (response.ok) {
              return response.json();
            }
            return null;
          }),
          
          // Load config item details (stored per company)
          apiCache.get(`config-item-details-${companyId}`, async () => {
            const response = await fetch(
              `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/company-data/${companyId}/config-item-details`,
              {
                headers: {
                  'Authorization': `Bearer ${accessToken}`,
                },
              }
            );
            if (response.ok) {
              return response.json();
            }
            return null;
          })
        ]);

        if (companyData) {
          setDiscoveryOverride(companyData.discoveryProgressOverride ?? null);
          setImplementationOverride(companyData.implementationProgressOverride ?? null);
        }

        if (configData) {
          setDefaultConfigItems(configData.items || []);
        }

        if (detailsData) {
          setConfigItemDetails(detailsData.details || {});
        }
      } catch (error) {
        console.error('Error loading configuration data:', error);
      } finally {
        setConfigItemsLoading(false);
      }
    };

    loadData();
  }, [companyId, accessToken, refreshKey, localRefreshKey]);

  // Save override to backend
  const saveOverride = async (type: 'discovery' | 'implementation', value: number | null) => {
    setSaving(true);
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/company-data/${companyId}/progress-override`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            type,
            value
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        if (response.status === 404) {
          alert('Please save some configuration data before setting a progress override.');
        } else {
          alert(`Failed to save progress override: ${errorData.error || 'Unknown error'}`);
        }
        throw new Error('Failed to save override');
      }

      // Invalidate cache after save
      apiCache.invalidate(`company-data-${companyId}`);

      if (type === 'discovery') {
        setDiscoveryOverride(value);
      } else {
        setImplementationOverride(value);
      }
    } catch (error) {
      console.error('Error saving progress override:', error);
    } finally {
      setSaving(false);
    }
  };

  // Save configuration item details (memoized callback)
  const handleSaveConfigItemDetails = useCallback(async (details: any) => {
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/company-data/${companyId}/config-item-details`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
          },
          body: JSON.stringify({ details }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to save configuration item details');
      }

      // Invalidate cache after save
      apiCache.invalidate(`config-item-details-${companyId}`);

      // Update local state
      setConfigItemDetails(prev => ({
        ...prev,
        [details.configItemId]: details
      }));

      // Trigger refresh to recalculate progress
      setLocalRefreshKey(prev => prev + 1);
    } catch (error) {
      console.error('Error saving config item details:', error);
      throw error;
    }
  }, [companyId, accessToken]);

  const handleSaveDiscoveryOverride = () => {
    const value = parseInt(tempDiscoveryValue);
    if (isNaN(value) || value < 0 || value > 100) {
      alert('Please enter a valid percentage between 0 and 100');
      return;
    }
    saveOverride('discovery', value);
    setShowDiscoveryOverrideDialog(false);
    setTempDiscoveryValue('');
  };

  const handleClearDiscoveryOverride = () => {
    saveOverride('discovery', null);
    setShowDiscoveryOverrideDialog(false);
    setTempDiscoveryValue('');
  };

  const handleSaveImplementationOverride = () => {
    const value = parseInt(tempImplementationValue);
    if (isNaN(value) || value < 0 || value > 100) {
      alert('Please enter a valid percentage between 0 and 100');
      return;
    }
    saveOverride('implementation', value);
    setShowImplementationOverrideDialog(false);
    setTempImplementationValue('');
  };

  const handleClearImplementationOverride = () => {
    saveOverride('implementation', null);
    setShowImplementationOverrideDialog(false);
    setTempImplementationValue('');
  };

  const getConfigItemStatus = useCallback((itemId: string) => {
    const details = configItemDetails[itemId];
    if (!details) return 'not-started';
    return details.status || 'not-started';
  }, [configItemDetails]);

  const getConfigItemStatusIcon = useCallback((status: string) => {
    switch (status) {
      case 'done':
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'in-review':
        return <CheckCircle className="h-4 w-4 text-purple-600" />;
      case 'ready-for-implementation':
        return <CheckCircle className="h-4 w-4 text-blue-600" />;
      case 'in-progress':
        return <AlertCircle className="h-4 w-4 text-yellow-600" />;
      default:
        return <Circle className="h-4 w-4 text-gray-400" />;
    }
  }, []);

  const getConfigItemStatusBadge = useCallback((status: string) => {
    switch (status) {
      case 'done':
        return <Badge className="bg-green-600 text-xs">Done</Badge>;
      case 'in-review':
        return <Badge className="bg-purple-600 text-xs">In Review</Badge>;
      case 'ready-for-implementation':
        return <Badge className="bg-blue-600 text-xs">Ready for Implementation</Badge>;
      case 'in-progress':
        return <Badge variant="secondary" className="text-xs">In Progress</Badge>;
      default:
        return null;
    }
  }, []);

  // Calculate configuration status (memoized for performance)
  const configItems = useMemo((): ConfigurationItem[] => {
    const items: ConfigurationItem[] = [];

    // 1. Jira Spaces/Projects
    const projectsCount = projects?.length || 0;
    const projectsWithUrls = projects?.filter(p => p.jiraSpaceUrl).length || 0;
    const projectsImplemented = projects?.filter(p => p.implemented).length || 0;
    
    let spacesQuality = 0;
    if (projectsCount > 0) {
      spacesQuality = Math.round(
        (projectsCount >= 1 ? 40 : 0) + // Has at least one space
        (projectsWithUrls / Math.max(projectsCount, 1)) * 60 // URLs configured
      );
    }
    const spacesImplementation = projectsCount > 0 ? Math.round((projectsImplemented / projectsCount) * 100) : 0;
    
    items.push({
      id: 'projects',
      name: 'Jira Spaces',
      description: 'Configure Jira Service Management spaces/projects',
      status: projectsCount === 0 ? 'incomplete' : spacesQuality >= 80 ? 'complete' : 'partial',
      count: projectsCount,
      implementedCount: projectsImplemented,
      qualityScore: spacesQuality,
      implementationPercent: spacesImplementation,
      icon: Package,
      details: [
        `${projectsCount} Jira ${projectsCount === 1 ? 'space' : 'spaces'} configured`,
        `${projectsWithUrls} ${projectsWithUrls === 1 ? 'space has' : 'spaces have'} Jira URLs`,
        `${projectsImplemented} ${projectsImplemented === 1 ? 'space' : 'spaces'} marked as implemented`,
        projectsCount === 0 ? 'No spaces created yet' : ''
      ].filter(Boolean),
      recommendations: projectsCount === 0 
        ? ['Create at least one Jira space to get started', 'Each space represents a Jira Service Management project']
        : projectsWithUrls < projectsCount
        ? ['Add Jira Space URLs to connect with actual Jira projects']
        : ['Spaces configured successfully'],
      typicalRange: TYPICAL_RANGES.spaces.description
    });

    // 2. Teams
    const totalTeams = projects?.reduce((sum, p) => sum + (p.teams?.length || 0), 0) || 0;
    const teamsWithRequests = projects?.reduce((sum, p) => 
      sum + (p.teams?.filter(t => (t.requests?.length || 0) > 0).length || 0), 0
    ) || 0;
    const teamsImplemented = projects?.reduce((sum, p) => 
      sum + (p.teams?.filter(t => t.implemented).length || 0), 0
    ) || 0;
    
    let teamsQuality = 0;
    if (totalTeams > 0) {
      teamsQuality = Math.round(
        (totalTeams >= 2 ? 40 : totalTeams >= 1 ? 20 : 0) + // Has teams
        (teamsWithRequests / Math.max(totalTeams, 1)) * 60 // Teams have requests
      );
    }
    const teamsImplementation = totalTeams > 0 ? Math.round((teamsImplemented / totalTeams) * 100) : 0;
    
    items.push({
      id: 'teams',
      name: 'Teams',
      description: 'Configure teams to organize request types',
      status: totalTeams === 0 ? 'incomplete' : teamsQuality >= 80 ? 'complete' : 'partial',
      count: totalTeams,
      implementedCount: teamsImplemented,
      qualityScore: teamsQuality,
      implementationPercent: teamsImplementation,
      icon: Users,
      details: [
        `${totalTeams} ${totalTeams === 1 ? 'team' : 'teams'} configured`,
        `${teamsWithRequests} ${teamsWithRequests === 1 ? 'team has' : 'teams have'} request types`,
        `${teamsImplemented} ${teamsImplemented === 1 ? 'team' : 'teams'} marked as implemented`,
        totalTeams === 0 ? 'No teams created yet' : ''
      ].filter(Boolean),
      recommendations: totalTeams === 0
        ? ['Create teams to organize your service desk request types', 'Common teams: IT Support, HR, Facilities, Finance']
        : teamsWithRequests === 0
        ? ['Add request types to your teams']
        : ['Teams configured successfully'],
      typicalRange: TYPICAL_RANGES.teams.description
    });

    // 3. Workflows
    const totalWorkflows = projects?.reduce((sum, p) => sum + (p.workflows?.length || 0), 0) || 0;
    const workflowsWithStatuses = projects?.reduce((sum, p) => 
      sum + (p.workflows?.filter(w => (w.statuses?.length || 0) >= 3).length || 0), 0
    ) || 0;
    const workflowsImplemented = projects?.reduce((sum, p) => 
      sum + (p.workflows?.filter(w => w.implemented).length || 0), 0
    ) || 0;
    
    let workflowsQuality = 0;
    if (totalWorkflows > 0) {
      workflowsQuality = Math.round(
        (totalWorkflows >= 2 ? 40 : totalWorkflows >= 1 ? 20 : 0) + // Has workflows
        (workflowsWithStatuses / Math.max(totalWorkflows, 1)) * 60 // Complete status definitions
      );
    }
    const workflowsImplementation = totalWorkflows > 0 ? Math.round((workflowsImplemented / totalWorkflows) * 100) : 0;
    
    items.push({
      id: 'workflows',
      name: 'Workflows',
      description: 'Define status workflows for request processing',
      status: totalWorkflows === 0 ? 'incomplete' : workflowsQuality >= 80 ? 'complete' : 'partial',
      count: totalWorkflows,
      implementedCount: workflowsImplemented,
      qualityScore: workflowsQuality,
      implementationPercent: workflowsImplementation,
      icon: Workflow,
      details: [
        `${totalWorkflows} ${totalWorkflows === 1 ? 'workflow' : 'workflows'} configured`,
        `${workflowsWithStatuses} ${workflowsWithStatuses === 1 ? 'workflow has' : 'workflows have'} complete status definitions`,
        `${workflowsImplemented} ${workflowsImplemented === 1 ? 'workflow' : 'workflows'} marked as implemented`,
        totalWorkflows === 0 ? 'No workflows created yet' : ''
      ].filter(Boolean),
      recommendations: totalWorkflows === 0
        ? ['Create workflows to define how requests move through statuses', 'Recommended: At least Open, In Progress, and Closed statuses']
        : workflowsWithStatuses === 0
        ? ['Add at least 3 statuses to each workflow (Open, In Progress, Closed)']
        : ['Workflows configured successfully'],
      typicalRange: TYPICAL_RANGES.workflows.description
    });

    // 4. Work Types
    const totalWorkTypes = projects?.reduce((sum, p) => sum + (p.workTypes?.length || 0), 0) || 0;
    const workTypesWithWorkflows = projects?.reduce((sum, p) => 
      sum + (p.workTypes?.filter(wt => wt.workflowId).length || 0), 0
    ) || 0;
    const workTypesImplemented = projects?.reduce((sum, p) => 
      sum + (p.workTypes?.filter(wt => wt.implemented).length || 0), 0
    ) || 0;
    
    let workTypesQuality = 0;
    if (totalWorkTypes > 0) {
      workTypesQuality = Math.round(
        (totalWorkTypes >= 3 ? 40 : totalWorkTypes >= 1 ? 20 : 0) + // Has work types
        (workTypesWithWorkflows / Math.max(totalWorkTypes, 1)) * 60 // Linked to workflows
      );
    }
    const workTypesImplementation = totalWorkTypes > 0 ? Math.round((workTypesImplemented / totalWorkTypes) * 100) : 0;
    
    items.push({
      id: 'workTypes',
      name: 'Work Types',
      description: 'Define work types that map to request types',
      status: totalWorkTypes === 0 ? 'incomplete' : workTypesQuality >= 80 ? 'complete' : 'partial',
      count: totalWorkTypes,
      implementedCount: workTypesImplemented,
      qualityScore: workTypesQuality,
      implementationPercent: workTypesImplementation,
      icon: GitBranch,
      details: [
        `${totalWorkTypes} work ${totalWorkTypes === 1 ? 'type' : 'types'} configured`,
        `${workTypesWithWorkflows} work ${workTypesWithWorkflows === 1 ? 'type' : 'types'} linked to workflows`,
        `${workTypesImplemented} work ${workTypesImplemented === 1 ? 'type' : 'types'} marked as implemented`,
        totalWorkTypes === 0 ? 'No work types created yet' : ''
      ].filter(Boolean),
      recommendations: totalWorkTypes === 0
        ? ['Create work types to categorize your service requests', 'Each request type must be mapped to a work type']
        : workTypesWithWorkflows < totalWorkTypes
        ? ['Assign workflows to all work types']
        : ['Work types configured successfully'],
      typicalRange: TYPICAL_RANGES.workTypes.description
    });

    // 5. Request Types
    const totalRequests = projects?.reduce((sum, p) => 
      sum + (p.teams?.reduce((teamSum, t) => teamSum + (t.requests?.length || 0), 0) || 0), 0
    ) || 0;
    const requestsWithWorkTypes = projects?.reduce((sum, p) => 
      sum + (p.teams?.reduce((teamSum, t) => 
        teamSum + (t.requests?.filter(r => r.workTypeId).length || 0), 0
      ) || 0), 0
    ) || 0;
    const requestsWithFields = projects?.reduce((sum, p) => 
      sum + (p.teams?.reduce((teamSum, t) => 
        teamSum + (t.requests?.filter(r => (r.fields?.length || 0) > 0).length || 0), 0
      ) || 0), 0
    ) || 0;
    const requestsImplemented = projects?.reduce((sum, p) => 
      sum + (p.teams?.reduce((teamSum, t) => 
        teamSum + (t.requests?.filter(r => r.implementationStatus === 'approved' || r.implementationStatus === 'implemented').length || 0), 0
      ) || 0), 0
    ) || 0;
    
    let requestsQuality = 0;
    if (totalRequests > 0) {
      requestsQuality = Math.round(
        (totalRequests >= 5 ? 30 : totalRequests >= 1 ? 15 : 0) + // Has request types
        (requestsWithWorkTypes / Math.max(totalRequests, 1)) * 35 + // Linked to work types
        (requestsWithFields / Math.max(totalRequests, 1)) * 35 // Have fields
      );
    }
    const requestsImplementation = totalRequests > 0 ? Math.round((requestsImplemented / totalRequests) * 100) : 0;
    
    items.push({
      id: 'requests',
      name: 'Request Types',
      description: 'Configure service request types with fields',
      status: totalRequests === 0 ? 'incomplete' : requestsQuality >= 80 ? 'complete' : 'partial',
      count: totalRequests,
      implementedCount: requestsImplemented,
      qualityScore: requestsQuality,
      implementationPercent: requestsImplementation,
      icon: List,
      details: [
        `${totalRequests} request ${totalRequests === 1 ? 'type' : 'types'} configured`,
        `${requestsWithWorkTypes} request ${requestsWithWorkTypes === 1 ? 'type' : 'types'} linked to work types`,
        `${requestsWithFields} request ${requestsWithFields === 1 ? 'type has' : 'types have'} custom fields`,
        `${requestsImplemented} request ${requestsImplemented === 1 ? 'type' : 'types'} marked as implemented`,
        totalRequests === 0 ? 'No request types created yet' : ''
      ].filter(Boolean),
      recommendations: totalRequests === 0
        ? ['Create request types for services you want to offer', 'Examples: Password Reset, Hardware Request, Access Request']
        : requestsWithWorkTypes < totalRequests
        ? ['Assign work types to all request types']
        : requestsWithFields === 0
        ? ['Add custom fields to capture specific information for each request type']
        : ['Request types configured successfully'],
      typicalRange: TYPICAL_RANGES.requestTypes.description
    });

    // 6. Fields
    const totalFields = projects?.reduce((sum, p) => 
      sum + (p.teams?.reduce((teamSum, t) => 
        teamSum + (t.requests?.reduce((reqSum, r) => reqSum + (r.fields?.length || 0), 0) || 0), 0
      ) || 0), 0
    ) || 0;
    const requestsReady = projects?.reduce((sum, p) => 
      sum + (p.teams?.reduce((teamSum, t) => 
        teamSum + (t.requests?.filter(r => r.readyForImplementation).length || 0), 0
      ) || 0), 0
    ) || 0;
    const averageFieldsPerRequest = totalRequests > 0 ? Math.round((totalFields / totalRequests) * 10) / 10 : 0;
    
    let fieldsQuality = 0;
    if (totalFields > 0 && totalRequests > 0) {
      const fieldsPerRequest = totalFields / totalRequests;
      fieldsQuality = Math.round(
        (totalFields >= 1 ? 30 : 0) + // Has fields
        (Math.min(fieldsPerRequest / 3, 1)) * 40 + // Average 3+ fields per request
        (requestsReady / Math.max(totalRequests, 1)) * 30 // Requests marked ready
      );
    }
    
    items.push({
      id: 'fields',
      name: 'Form Fields',
      description: 'Define custom fields for request forms',
      status: totalFields === 0 ? 'incomplete' : fieldsQuality >= 80 ? 'complete' : 'partial',
      count: totalFields,
      qualityScore: fieldsQuality,
      icon: FileText,
      details: [
        `${totalFields} ${totalFields === 1 ? 'field' : 'fields'} configured`,
        totalRequests > 0 ? `Average ${averageFieldsPerRequest} fields per request type` : '',
        `${requestsReady} request ${requestsReady === 1 ? 'type' : 'types'} marked ready for implementation`,
        totalFields === 0 ? 'No fields created yet' : ''
      ].filter(Boolean),
      recommendations: totalFields === 0
        ? ['Add fields to request types to capture required information', 'Use different field types: text, dropdown, date, etc.']
        : requestsReady === 0
        ? ['Mark request types as "Ready for Implementation" when complete']
        : ['Fields configured successfully'],
      typicalRange: TYPICAL_RANGES.fieldsPerRequest.description
    });

    return items;
  }, [projects]); // Memoize based on projects data
  
  // Calculate overall Discovery/Data Gathering Progress based on quality scores (memoized)
  // Weight Request Types and Fields more heavily as they represent the bulk of discovery work
  const { calculatedDiscoveryProgress, calculatedImplementationProgress } = useMemo(() => {
    const spacesItem = configItems.find(item => item.id === 'projects');
    const teamsItem = configItems.find(item => item.id === 'teams');
    const workflowsItem = configItems.find(item => item.id === 'workflows');
    const workTypesItem = configItems.find(item => item.id === 'workTypes');
    const requestsItem = configItems.find(item => item.id === 'requests');
    const fieldsItem = configItems.find(item => item.id === 'fields');
    
    const calculatedDiscoveryProgress = Math.round(
      (spacesItem?.qualityScore || 0) * 0.10 +    // 10% - Basic structural setup
      (teamsItem?.qualityScore || 0) * 0.10 +      // 10% - Organizational structure
      (workflowsItem?.qualityScore || 0) * 0.15 +  // 15% - Status and transition definitions
      (workTypesItem?.qualityScore || 0) * 0.10 +  // 10% - Request categorization
      (requestsItem?.qualityScore || 0) * 0.35 +   // 35% - Core discovery work (most important)
      (fieldsItem?.qualityScore || 0) * 0.20       // 20% - Detailed form definitions
    );
    
    // Calculate Implementation Progress (average of implementation percentages, excluding fields which don't have implementation tracking)
    const itemsWithImplementation = configItems.filter(item => item.implementationPercent !== undefined);
    const calculatedImplementationProgress = itemsWithImplementation.length > 0
      ? Math.round(
          itemsWithImplementation.reduce((sum, item) => sum + (item.implementationPercent || 0), 0) / itemsWithImplementation.length
        )
      : 0;

    return { calculatedDiscoveryProgress, calculatedImplementationProgress };
  }, [configItems]);

  // Use manual override if set, otherwise use calculated value
  const discoveryProgress = discoveryOverride !== null ? discoveryOverride : calculatedDiscoveryProgress;
  const implementationProgress = implementationOverride !== null ? implementationOverride : calculatedImplementationProgress;
  
  // Count items for quick stats
  const totalItems = configItems.length;
  const completeItems = configItems.filter(item => item.status === 'complete').length;

  const getStatusIcon = useCallback((status: 'complete' | 'partial' | 'incomplete') => {
    switch (status) {
      case 'complete':
        return <CheckCircle className="h-5 w-5 text-green-600" />;
      case 'partial':
        return <AlertCircle className="h-5 w-5 text-yellow-600" />;
      case 'incomplete':
        return <Circle className="h-5 w-5 text-gray-400" />;
    }
  }, []);

  const getStatusBadge = useCallback((status: 'complete' | 'partial' | 'incomplete') => {
    switch (status) {
      case 'complete':
        return <Badge variant="default" className="bg-green-600">Complete</Badge>;
      case 'partial':
        return <Badge variant="secondary" className="bg-yellow-100 text-yellow-800 border-yellow-300">In Progress</Badge>;
      case 'incomplete':
        return <Badge variant="outline">Not Started</Badge>;
    }
  }, []);

  return (
    <div className="space-y-6">
      {/* Overall Progress - Two Phase Tracking */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Discovery/Data Gathering Progress */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Target className="h-5 w-5" />
                Discovery & Data Gathering
              </div>
              {canOverride && (
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => {
                    setTempDiscoveryValue(discoveryOverride?.toString() || '');
                    setShowDiscoveryOverrideDialog(true);
                  }}
                  className="h-8"
                >
                  <Edit className="h-3.5 w-3.5 mr-1" />
                  Override
                </Button>
              )}
            </CardTitle>
            <CardDescription>
              Configuration quality and completeness
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span>{discoveryProgress}% Complete</span>
                  {discoveryOverride !== null && (
                    <Badge variant="secondary" className="text-xs">
                      Manual Override
                    </Badge>
                  )}
                  <button 
                    onClick={() => setShowGuidanceModal(true)}
                    className="text-primary underline hover:no-underline text-sm"
                  >
                    <Info className="h-3.5 w-3.5 inline mr-1" />
                    guidance
                  </button>
                </div>
                <Badge variant={discoveryProgress >= 80 ? 'default' : discoveryProgress > 40 ? 'secondary' : 'outline'}>
                  {discoveryProgress >= 80 ? 'Ready' : discoveryProgress > 40 ? 'Good Progress' : 'Getting Started'}
                </Badge>
              </div>
              <Progress value={discoveryProgress} className="h-3" />
              {discoveryOverride !== null && (
                <p className="text-xs text-muted-foreground">
                  Calculated: {calculatedDiscoveryProgress}% • Using manual override: {discoveryOverride}%
                </p>
              )}
            </div>

            {discoveryProgress >= 80 && (
              <Alert className="mt-4 border-green-200 bg-green-50">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <AlertDescription className="text-green-800">
                  Discovery phase is well-developed. Configuration items have high quality and completeness.
                </AlertDescription>
              </Alert>
            )}

            {discoveryProgress >= 40 && discoveryProgress < 80 && (
              <Alert className="mt-4">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  Good progress on discovery. Continue adding details and refining configuration items.
                </AlertDescription>
              </Alert>
            )}

            {discoveryProgress < 40 && (
              <Alert className="mt-4 border-blue-200 bg-blue-50">
                <AlertCircle className="h-4 w-4 text-blue-600" />
                <AlertDescription className="text-blue-800">
                  Discovery is in early stages. Focus on creating core configuration items and adding details.
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>

        {/* Implementation Progress */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Rocket className="h-5 w-5" />
                Implementation Phase
              </div>
              {canOverride && (
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => {
                    setTempImplementationValue(implementationOverride?.toString() || '');
                    setShowImplementationOverrideDialog(true);
                  }}
                  className="h-8"
                >
                  <Edit className="h-3.5 w-3.5 mr-1" />
                  Override
                </Button>
              )}
            </CardTitle>
            <CardDescription>
              Items marked as implemented in Jira
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <span>{implementationProgress}% Implemented</span>
                {implementationOverride !== null && (
                  <Badge variant="secondary" className="text-xs">
                    Manual Override
                  </Badge>
                )}
                <div className="flex-1" />
                <Badge variant={implementationProgress === 100 ? 'default' : implementationProgress > 0 ? 'secondary' : 'outline'}>
                  {implementationProgress === 100 ? 'Fully Deployed' : implementationProgress > 0 ? 'In Progress' : 'Not Started'}
                </Badge>
              </div>
              <Progress value={implementationProgress} className="h-3" />
              {implementationOverride !== null && (
                <p className="text-xs text-muted-foreground">
                  Calculated: {calculatedImplementationProgress}% • Using manual override: {implementationOverride}%
                </p>
              )}
              <p className="text-muted-foreground mt-3">
                Track deployment progress as items are implemented in the live Jira environment. Only Consultants can mark items as implemented.
              </p>
            </div>

            {implementationProgress === 100 && (
              <Alert className="mt-4 border-green-200 bg-green-50">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <AlertDescription className="text-green-800">
                  Implementation complete! All configured items have been deployed to Jira.
                </AlertDescription>
              </Alert>
            )}

            {implementationProgress > 0 && implementationProgress < 100 && (
              <Alert className="mt-4">
                <Rocket className="h-4 w-4" />
                <AlertDescription>
                  Implementation is in progress. Continue marking items as implemented as they are deployed.
                </AlertDescription>
              </Alert>
            )}

            {implementationProgress === 0 && (
              <Alert className="mt-4 border-blue-200 bg-blue-50">
                <AlertCircle className="h-4 w-4 text-blue-600" />
                <AlertDescription className="text-blue-800">
                  No items implemented yet. Begin deploying configurations to Jira when discovery is complete.
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-muted-foreground">Total Users</p>
                <p className="text-muted-foreground">{totalUsers}</p>
              </div>
              <Users className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-muted-foreground">Jira Spaces</p>
                <p className="text-muted-foreground">{projects.length}</p>
              </div>
              <Package className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-muted-foreground">Request Types</p>
                <p className="text-muted-foreground">
                  {projects.reduce((sum, p) => 
                    sum + p.teams.reduce((teamSum, t) => teamSum + t.requests.length, 0), 0
                  )}
                </p>
              </div>
              <List className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recommended Next Steps */}
      <Card>
        <CardHeader>
          <CardTitle>Recommended Next Steps</CardTitle>
          <CardDescription>
            Priority actions to move the configuration forward
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {configItems
              .filter(item => item.status !== 'complete')
              .sort((a, b) => {
                // Prioritize incomplete over partial
                if (a.status === 'incomplete' && b.status === 'partial') return -1;
                if (a.status === 'partial' && b.status === 'incomplete') return 1;
                return 0;
              })
              .slice(0, 3)
              .map((item, index) => (
                <div key={item.id} className="flex items-start gap-3 p-3 border rounded-lg">
                  <div className="flex items-center justify-center w-6 h-6 rounded-full bg-primary text-primary-foreground flex-shrink-0">
                    {index + 1}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <item.icon className="h-4 w-4" />
                      <span>{item.name}</span>
                      {getStatusBadge(item.status)}
                    </div>
                    <p className="text-muted-foreground">{item.recommendations[0]}</p>
                  </div>
                </div>
              ))}
            
            {configItems.every(item => item.status === 'complete') && (
              <div className="text-center py-6">
                <CheckCircle className="h-12 w-12 text-green-600 mx-auto mb-3" />
                <p className="text-green-800">
                  All configuration areas are well-developed. The company is ready for Jira implementation.
                </p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Unified Configuration Checklist - Organized by Phase */}
      <div className="space-y-3">
        <h3>Configuration Checklist</h3>
        <p className="text-muted-foreground">
          Review each configuration area and follow the recommendations to complete your setup. Each client engagement is unique, so use the typical ranges as guidance rather than strict targets.
        </p>

        {configItemsLoading && (
          <Card>
            <CardContent className="py-12 text-center">
              <p className="text-muted-foreground">Loading configuration items...</p>
            </CardContent>
          </Card>
        )}

        {!configItemsLoading && (
          <>
            {/* Phase 1: Core Foundation */}
            <Card className="mt-4">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Target className="h-5 w-5" />
                  Phase 1: Core Foundation
                </CardTitle>
                <CardDescription>
                  Essential setup items for your service desk foundation
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {/* Structural Items - Phase 1 */}
                  {configItems.filter(item => 
                    ['projects', 'teams', 'workflows', 'workTypes', 'requests'].includes(item.id)
                  ).map((item) => (
                    <div 
                      key={item.id}
                      className="flex items-start gap-3 p-3 border rounded-lg hover:border-primary cursor-pointer transition-colors"
                      onClick={() => setExpandedSection(expandedSection === item.id ? null : item.id)}
                    >
                      <div className="pt-0.5">
                        {getStatusIcon(item.status)}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <div className="flex items-center gap-2">
                            <item.icon className="h-4 w-4" />
                            <span>{item.name}</span>
                          </div>
                          <Badge variant="default" className="text-xs">
                            Core Feature
                          </Badge>
                          <Badge variant="outline" className="text-xs">
                            {item.count} configured
                          </Badge>
                          {item.implementedCount !== undefined && item.implementedCount > 0 && (
                            <Badge variant="secondary" className="text-xs">
                              {item.implementedCount} implemented
                            </Badge>
                          )}
                          {getStatusBadge(item.status)}
                        </div>
                        <p className="text-muted-foreground text-sm">{item.description}</p>
                        
                        {/* Expanded Details */}
                        {expandedSection === item.id && (
                          <div className="mt-4 space-y-4 pt-4 border-t">
                            <div>
                              <h4 className="mb-2">Current Status</h4>
                              <ul className="space-y-1">
                                {item.details.map((detail, index) => (
                                  <li key={index} className="text-muted-foreground flex items-center gap-2 text-sm">
                                    <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground" />
                                    {detail}
                                  </li>
                                ))}
                              </ul>
                            </div>

                            {item.typicalRange && (
                              <div>
                                <h4 className="mb-2">Typical Range</h4>
                                <p className="text-muted-foreground text-sm">{item.typicalRange}</p>
                              </div>
                            )}

                            <div>
                              <h4 className="mb-2">Recommendations</h4>
                              <div className="space-y-2">
                                {item.recommendations.map((rec, index) => (
                                  <Alert key={index} className={
                                    item.status === 'complete' 
                                      ? 'border-green-200 bg-green-50' 
                                      : 'border-blue-200 bg-blue-50'
                                  }>
                                    <AlertDescription className={
                                      item.status === 'complete' ? 'text-green-800' : 'text-blue-800'
                                    }>
                                      {rec}
                                    </AlertDescription>
                                  </Alert>
                                ))}
                              </div>
                            </div>

                            <div className="pt-2">
                              <Button 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (item.id === 'projects' && onNavigateToProjects) onNavigateToProjects();
                                  if (item.id === 'teams' && onNavigateToTeams) onNavigateToTeams();
                                  if (item.id === 'workflows' && onNavigateToWorkflows) onNavigateToWorkflows();
                                  if (item.id === 'workTypes' && onNavigateToWorkTypes) onNavigateToWorkTypes();
                                  if (item.id === 'requests' && onNavigateToRequests) onNavigateToRequests();
                                }}
                                className="w-full"
                              >
                                Manage {item.name}
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                      <ChevronRight className={`h-4 w-4 text-muted-foreground flex-shrink-0 transition-transform ${
                        expandedSection === item.id ? 'rotate-90' : ''
                      }`} />
                    </div>
                  ))}

                  {/* Additional Config Items - Phase 1 */}
                  {defaultConfigItems
                    .filter(item => item.effectivePhase === 1 && item.effectiveEnabled)
                    .map(item => {
                      const itemStatus = getConfigItemStatus(item.id);
                      const itemDetails = configItemDetails[item.id];
                      
                      return (
                        <div 
                          key={item.id} 
                          className="flex items-start gap-3 p-3 border rounded-lg hover:border-primary cursor-pointer transition-colors"
                          onClick={() => {
                            setSelectedConfigItem(item);
                            setShowConfigItemDialog(true);
                          }}
                        >
                          <div className="pt-0.5">
                            {getConfigItemStatusIcon(itemStatus)}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                              <span>{item.name}</span>
                              {getConfigItemStatusBadge(itemStatus)}
                              {item.coreFeature && (
                                <Badge variant="default" className="text-xs">
                                  Core Feature
                                </Badge>
                              )}
                              {item.requiresAssets && (
                                <Badge variant="secondary" className="text-xs">
                                  Requires Assets
                                </Badge>
                              )}
                              {item.override?.assignedTo && (
                                <Badge variant="outline" className="text-xs">
                                  Assigned to {item.override.assignedTo.userName}
                                </Badge>
                              )}
                            </div>
                            <p className="text-muted-foreground text-sm">{item.description}</p>
                            {itemDetails?.configurationDetails && (
                              <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                                {itemDetails.configurationDetails}
                              </p>
                            )}
                            {item.override?.notes && (
                              <p className="text-xs text-muted-foreground mt-1 italic">
                                Note: {item.override.notes}
                              </p>
                            )}
                          </div>
                          <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                        </div>
                      );
                    })}
                </div>
              </CardContent>
            </Card>

            {/* Phase 2: Automation & Integration */}
            {(() => {
              const phase2ConfigItems = defaultConfigItems
                .filter(item => item.effectivePhase === 2 && item.effectiveEnabled);
              
              const phase2StructuralItems = configItems.filter(item => item.id === 'fields');
              
              if (phase2ConfigItems.length === 0 && phase2StructuralItems.length === 0) return null;

              return (
                <Card className="mt-4">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <TrendingUp className="h-5 w-5" />
                      Phase 2: Automation & Efficiency
                    </CardTitle>
                    <CardDescription>
                      Automate workflows and streamline operations
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {/* Fields (structural item in Phase 2) */}
                      {phase2StructuralItems.map((item) => (
                        <div 
                          key={item.id}
                          className="flex items-start gap-3 p-3 border rounded-lg hover:border-primary cursor-pointer transition-colors"
                          onClick={() => setExpandedSection(expandedSection === item.id ? null : item.id)}
                        >
                          <div className="pt-0.5">
                            {getStatusIcon(item.status)}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                              <div className="flex items-center gap-2">
                                <item.icon className="h-4 w-4" />
                                <span>{item.name}</span>
                              </div>
                              <Badge variant="default" className="text-xs">
                                Core Feature
                              </Badge>
                              <Badge variant="outline" className="text-xs">
                                {item.count} configured
                              </Badge>
                              {getStatusBadge(item.status)}
                            </div>
                            <p className="text-muted-foreground text-sm">{item.description}</p>
                            
                            {expandedSection === item.id && (
                              <div className="mt-4 space-y-4 pt-4 border-t">
                                <div>
                                  <h4 className="mb-2">Current Status</h4>
                                  <ul className="space-y-1">
                                    {item.details.map((detail, index) => (
                                      <li key={index} className="text-muted-foreground flex items-center gap-2 text-sm">
                                        <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground" />
                                        {detail}
                                      </li>
                                    ))}
                                  </ul>
                                </div>

                                {item.typicalRange && (
                                  <div>
                                    <h4 className="mb-2">Typical Range</h4>
                                    <p className="text-muted-foreground text-sm">{item.typicalRange}</p>
                                  </div>
                                )}

                                <div>
                                  <h4 className="mb-2">Recommendations</h4>
                                  <div className="space-y-2">
                                    {item.recommendations.map((rec, index) => (
                                      <Alert key={index} className={
                                        item.status === 'complete' 
                                          ? 'border-green-200 bg-green-50' 
                                          : 'border-blue-200 bg-blue-50'
                                      }>
                                        <AlertDescription className={
                                          item.status === 'complete' ? 'text-green-800' : 'text-blue-800'
                                        }>
                                          {rec}
                                        </AlertDescription>
                                      </Alert>
                                    ))}
                                  </div>
                                </div>

                                <div className="pt-2">
                                  <Button 
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (item.id === 'fields' && onNavigateToFields) onNavigateToFields();
                                    }}
                                    className="w-full"
                                  >
                                    Manage {item.name}
                                  </Button>
                                </div>
                              </div>
                            )}
                          </div>
                          <ChevronRight className={`h-4 w-4 text-muted-foreground flex-shrink-0 transition-transform ${
                            expandedSection === item.id ? 'rotate-90' : ''
                          }`} />
                        </div>
                      ))}

                      {/* Additional Config Items - Phase 2 */}
                      {phase2ConfigItems.map(item => {
                        const itemStatus = getConfigItemStatus(item.id);
                        const itemDetails = configItemDetails[item.id];
                        
                        return (
                          <div 
                            key={item.id} 
                            className="flex items-start gap-3 p-3 border rounded-lg hover:border-primary cursor-pointer transition-colors"
                            onClick={() => {
                              setSelectedConfigItem(item);
                              setShowConfigItemDialog(true);
                            }}
                          >
                            <div className="pt-0.5">
                              {getConfigItemStatusIcon(itemStatus)}
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1 flex-wrap">
                                <span>{item.name}</span>
                                {getConfigItemStatusBadge(itemStatus)}
                                {item.coreFeature && (
                                  <Badge variant="default" className="text-xs">
                                    Core Feature
                                  </Badge>
                                )}
                                {item.requiresAssets && (
                                  <Badge variant="secondary" className="text-xs">
                                    Requires Assets
                                  </Badge>
                                )}
                                {item.override?.assignedTo && (
                                  <Badge variant="outline" className="text-xs">
                                    Assigned to {item.override.assignedTo.userName}
                                  </Badge>
                                )}
                              </div>
                              <p className="text-muted-foreground text-sm">{item.description}</p>
                              {itemDetails?.configurationDetails && (
                                <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                                  {itemDetails.configurationDetails}
                                </p>
                              )}
                              {item.override?.notes && (
                                <p className="text-xs text-muted-foreground mt-1 italic">
                                  Note: {item.override.notes}
                                </p>
                              )}
                            </div>
                            <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              );
            })()}

            {/* Phase 3: Portal & User Experience */}
            {(() => {
              const phase3Items = defaultConfigItems
                .filter(item => item.effectivePhase === 3 && item.effectiveEnabled);
              
              if (phase3Items.length === 0) return null;

              return (
                <Card className="mt-4">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Rocket className="h-5 w-5" />
                      Phase 3: Portal & User Experience
                    </CardTitle>
                    <CardDescription>
                      Enhance the customer-facing portal and self-service capabilities
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {phase3Items.map(item => {
                        const itemStatus = getConfigItemStatus(item.id);
                        const itemDetails = configItemDetails[item.id];
                        
                        return (
                          <div 
                            key={item.id} 
                            className="flex items-start gap-3 p-3 border rounded-lg hover:border-primary cursor-pointer transition-colors"
                            onClick={() => {
                              setSelectedConfigItem(item);
                              setShowConfigItemDialog(true);
                            }}
                          >
                            <div className="pt-0.5">
                              {getConfigItemStatusIcon(itemStatus)}
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1 flex-wrap">
                                <span>{item.name}</span>
                                {getConfigItemStatusBadge(itemStatus)}
                                {item.coreFeature && (
                                  <Badge variant="default" className="text-xs">
                                    Core Feature
                                  </Badge>
                                )}
                                {item.requiresAssets && (
                                  <Badge variant="secondary" className="text-xs">
                                    Requires Assets
                                  </Badge>
                                )}
                                {item.override?.assignedTo && (
                                  <Badge variant="outline" className="text-xs">
                                    Assigned to {item.override.assignedTo.userName}
                                  </Badge>
                                )}
                              </div>
                              <p className="text-muted-foreground text-sm">{item.description}</p>
                              {itemDetails?.configurationDetails && (
                                <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                                  {itemDetails.configurationDetails}
                                </p>
                              )}
                              {item.override?.notes && (
                                <p className="text-xs text-muted-foreground mt-1 italic">
                                  Note: {item.override.notes}
                                </p>
                              )}
                            </div>
                            <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              );
            })()}

            {/* Phase 4: Advanced Features */}
            {(() => {
              const phase4Items = defaultConfigItems
                .filter(item => item.effectivePhase === 4 && item.effectiveEnabled);
              
              if (phase4Items.length === 0) return null;

              return (
                <Card className="mt-4">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Package className="h-5 w-5" />
                      Phase 4: Advanced Features
                    </CardTitle>
                    <CardDescription>
                      Advanced integrations, reporting, and optimization
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {phase4Items.map(item => {
                        const itemStatus = getConfigItemStatus(item.id);
                        const itemDetails = configItemDetails[item.id];
                        
                        return (
                          <div 
                            key={item.id} 
                            className="flex items-start gap-3 p-3 border rounded-lg hover:border-primary cursor-pointer transition-colors"
                            onClick={() => {
                              setSelectedConfigItem(item);
                              setShowConfigItemDialog(true);
                            }}
                          >
                            <div className="pt-0.5">
                              {getConfigItemStatusIcon(itemStatus)}
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1 flex-wrap">
                                <span>{item.name}</span>
                                {getConfigItemStatusBadge(itemStatus)}
                                {item.coreFeature && (
                                  <Badge variant="default" className="text-xs">
                                    Core Feature
                                  </Badge>
                                )}
                                {item.requiresAssets && (
                                  <Badge variant="secondary" className="text-xs">
                                    Requires Assets
                                  </Badge>
                                )}
                                {item.override?.assignedTo && (
                                  <Badge variant="outline" className="text-xs">
                                    Assigned to {item.override.assignedTo.userName}
                                  </Badge>
                                )}
                              </div>
                              <p className="text-muted-foreground text-sm">{item.description}</p>
                              {itemDetails?.configurationDetails && (
                                <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                                  {itemDetails.configurationDetails}
                                </p>
                              )}
                              {item.override?.notes && (
                                <p className="text-xs text-muted-foreground mt-1 italic">
                                  Note: {item.override.notes}
                                </p>
                              )}
                            </div>
                            <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              );
            })()}

            {/* Phase 5: Future Consideration */}
            {(() => {
              const phase5Items = defaultConfigItems
                .filter(item => item.effectivePhase === 5 && item.effectiveEnabled);
              
              if (phase5Items.length === 0) return null;

              return (
                <Card className="mt-4 border-gray-300 bg-gray-50">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-gray-700">
                      <Info className="h-5 w-5" />
                      Phase 5: Future Consideration
                    </CardTitle>
                    <CardDescription>
                      Items deferred for future implementation or out of current scope
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {phase5Items.map(item => {
                        const itemStatus = getConfigItemStatus(item.id);
                        const itemDetails = configItemDetails[item.id];
                        
                        return (
                          <div 
                            key={item.id} 
                            className="flex items-start gap-3 p-3 border rounded-lg hover:border-primary cursor-pointer transition-colors bg-white"
                            onClick={() => {
                              setSelectedConfigItem(item);
                              setShowConfigItemDialog(true);
                            }}
                          >
                            <div className="pt-0.5">
                              {getConfigItemStatusIcon(itemStatus)}
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1 flex-wrap">
                                <span>{item.name}</span>
                                {getConfigItemStatusBadge(itemStatus)}
                                <Badge variant="outline" className="text-xs bg-gray-100">
                                  Future
                                </Badge>
                                {item.coreFeature && (
                                  <Badge variant="default" className="text-xs">
                                    Core Feature
                                  </Badge>
                                )}
                                {item.requiresAssets && (
                                  <Badge variant="secondary" className="text-xs">
                                    Requires Assets
                                  </Badge>
                                )}
                                {item.override?.assignedTo && (
                                  <Badge variant="outline" className="text-xs">
                                    Assigned to {item.override.assignedTo.userName}
                                  </Badge>
                                )}
                              </div>
                              <p className="text-muted-foreground text-sm">{item.description}</p>
                              {itemDetails?.configurationDetails && (
                                <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                                  {itemDetails.configurationDetails}
                                </p>
                              )}
                              {item.override?.notes && (
                                <p className="text-xs text-muted-foreground mt-1 italic">
                                  Note: {item.override.notes}
                                </p>
                              )}
                            </div>
                            <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              );
            })()}
          </>
        )}
      </div>

      {/* Guidance Modal */}
      <Dialog open={showGuidanceModal} onOpenChange={setShowGuidanceModal}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Configuration Progress Guidance</DialogTitle>
            <DialogDescription>
              Understanding how we measure configuration quality and completeness
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 mt-4 pb-4">
            {/* Overview */}
            <div>
              <h4 className="mb-3">How Progress is Calculated</h4>
              <p className="text-muted-foreground mb-4">
                Each client engagement is unique, so we don't use fixed targets. Instead, we measure progress based on:
              </p>
              <ul className="space-y-2">
                <li className="text-muted-foreground flex items-start gap-2">
                  <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span><strong>Quality:</strong> Are items complete with all necessary details?</span>
                </li>
                <li className="text-muted-foreground flex items-start gap-2">
                  <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span><strong>Completeness:</strong> Do items have required relationships (workflows, fields, etc.)?</span>
                </li>
                <li className="text-muted-foreground flex items-start gap-2">
                  <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span><strong>Readiness:</strong> Are items ready for implementation?</span>
                </li>
              </ul>
            </div>

            {/* Typical Ranges */}
            <div>
              <h4 className="mb-3">Typical Implementation Ranges</h4>
              <p className="text-muted-foreground mb-4">
                These are typical ranges based on common service desk implementations. Your specific needs may differ:
              </p>
              <div className="space-y-2">
                <div className="p-3 border rounded-lg">
                  <div className="flex items-center justify-between mb-1">
                    <span>Jira Spaces</span>
                    <span className="text-muted-foreground">1-3</span>
                  </div>
                  <p className="text-sm text-muted-foreground">{TYPICAL_RANGES.spaces.description}</p>
                </div>
                <div className="p-3 border rounded-lg">
                  <div className="flex items-center justify-between mb-1">
                    <span>Teams</span>
                    <span className="text-muted-foreground">2-6</span>
                  </div>
                  <p className="text-sm text-muted-foreground">{TYPICAL_RANGES.teams.description}</p>
                </div>
                <div className="p-3 border rounded-lg">
                  <div className="flex items-center justify-between mb-1">
                    <span>Workflows</span>
                    <span className="text-muted-foreground">2-5</span>
                  </div>
                  <p className="text-sm text-muted-foreground">{TYPICAL_RANGES.workflows.description}</p>
                </div>
                <div className="p-3 border rounded-lg">
                  <div className="flex items-center justify-between mb-1">
                    <span>Work Types</span>
                    <span className="text-muted-foreground">3-10</span>
                  </div>
                  <p className="text-sm text-muted-foreground">{TYPICAL_RANGES.workTypes.description}</p>
                </div>
                <div className="p-3 border rounded-lg bg-blue-50 dark:bg-blue-950">
                  <div className="flex items-center justify-between mb-1">
                    <span>Request Types</span>
                    <span className="text-muted-foreground">5-30</span>
                  </div>
                  <p className="text-sm text-muted-foreground">{TYPICAL_RANGES.requestTypes.description}</p>
                </div>
                <div className="p-3 border rounded-lg bg-blue-50 dark:bg-blue-950">
                  <div className="flex items-center justify-between mb-1">
                    <span>Fields per Request</span>
                    <span className="text-muted-foreground">3-8</span>
                  </div>
                  <p className="text-sm text-muted-foreground">{TYPICAL_RANGES.fieldsPerRequest.description}</p>
                </div>
              </div>
            </div>

            {/* Weighting */}
            <div>
              <h4 className="mb-3">Discovery Focus Areas</h4>
              <p className="text-muted-foreground mb-4">
                Different areas require different amounts of discovery effort. The progress calculation weights categories accordingly:
              </p>
              <div className="space-y-2">
                <div className="flex items-center justify-between p-3 border rounded-lg bg-blue-50 dark:bg-blue-950">
                  <div>
                    <div>Request Types</div>
                    <div className="text-sm text-muted-foreground">Core discovery work</div>
                  </div>
                  <div className="text-2xl">35%</div>
                </div>
                <div className="flex items-center justify-between p-3 border rounded-lg bg-blue-50 dark:bg-blue-950">
                  <div>
                    <div>Fields</div>
                    <div className="text-sm text-muted-foreground">Detailed form definitions</div>
                  </div>
                  <div className="text-2xl">20%</div>
                </div>
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div>
                    <div>Workflows</div>
                    <div className="text-sm text-muted-foreground">Process definitions</div>
                  </div>
                  <div className="text-2xl">15%</div>
                </div>
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div>
                    <div>Spaces, Teams, Work Types</div>
                    <div className="text-sm text-muted-foreground">Structural setup</div>
                  </div>
                  <div className="text-2xl">30%</div>
                </div>
              </div>
            </div>

            {/* Important Note */}
            <Alert className="border-yellow-200 bg-yellow-50">
              <AlertCircle className="h-4 w-4 text-yellow-600" />
              <AlertDescription className="text-yellow-800">
                <strong>Remember:</strong> These are guidelines, not requirements. Your implementation should be tailored to your specific business needs and processes. Some clients need 5 request types, others need 50. The right number is what serves your organization best and fits within the scope of the SOW.
              </AlertDescription>
            </Alert>
          </div>
        </DialogContent>
      </Dialog>

      {/* Discovery Override Dialog */}
      <Dialog open={showDiscoveryOverrideDialog} onOpenChange={setShowDiscoveryOverrideDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Override Discovery Progress</DialogTitle>
            <DialogDescription>
              Manually set the discovery progress percentage for this company. This overrides the automatic calculation.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div>
              <Label htmlFor="discovery-override">Progress Percentage (0-100)</Label>
              <Input
                id="discovery-override"
                type="number"
                min="0"
                max="100"
                value={tempDiscoveryValue}
                onChange={(e) => setTempDiscoveryValue(e.target.value)}
                placeholder={calculatedDiscoveryProgress.toString()}
                className="mt-1"
              />
              <p className="text-sm text-muted-foreground mt-2">
                Calculated value: {calculatedDiscoveryProgress}%
              </p>
            </div>

            <Alert className="border-yellow-200 bg-yellow-50">
              <AlertCircle className="h-4 w-4 text-yellow-600" />
              <AlertDescription className="text-yellow-800 text-sm">
                Use this feature to manually adjust progress for early-stage engagements or when the automatic calculation doesn't reflect the actual progress.
              </AlertDescription>
            </Alert>

            <div className="flex gap-2 justify-end">
              {discoveryOverride !== null && (
                <Button
                  variant="outline"
                  onClick={handleClearDiscoveryOverride}
                  disabled={saving}
                >
                  <X className="h-4 w-4 mr-1" />
                  Clear Override
                </Button>
              )}
              <Button
                variant="outline"
                onClick={() => {
                  setShowDiscoveryOverrideDialog(false);
                  setTempDiscoveryValue('');
                }}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveDiscoveryOverride}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Override'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Implementation Override Dialog */}
      <Dialog open={showImplementationOverrideDialog} onOpenChange={setShowImplementationOverrideDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Override Implementation Progress</DialogTitle>
            <DialogDescription>
              Manually set the implementation progress percentage for this company. This overrides the automatic calculation.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div>
              <Label htmlFor="implementation-override">Progress Percentage (0-100)</Label>
              <Input
                id="implementation-override"
                type="number"
                min="0"
                max="100"
                value={tempImplementationValue}
                onChange={(e) => setTempImplementationValue(e.target.value)}
                placeholder={calculatedImplementationProgress.toString()}
                className="mt-1"
              />
              <p className="text-sm text-muted-foreground mt-2">
                Calculated value: {calculatedImplementationProgress}%
              </p>
            </div>

            <Alert className="border-yellow-200 bg-yellow-50">
              <AlertCircle className="h-4 w-4 text-yellow-600" />
              <AlertDescription className="text-yellow-800 text-sm">
                Track implementation progress independently of system-level item statuses.
              </AlertDescription>
            </Alert>

            <div className="flex gap-2 justify-end">
              {implementationOverride !== null && (
                <Button
                  variant="outline"
                  onClick={handleClearImplementationOverride}
                  disabled={saving}
                >
                  <X className="h-4 w-4 mr-1" />
                  Clear Override
                </Button>
              )}
              <Button
                variant="outline"
                onClick={() => {
                  setShowImplementationOverrideDialog(false);
                  setTempImplementationValue('');
                }}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveImplementationOverride}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Override'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Config Item Detail Dialog */}
      {selectedConfigItem && currentUser && (
        <ConfigItemDetailDialog
          item={selectedConfigItem}
          details={configItemDetails[selectedConfigItem.id] || null}
          isOpen={showConfigItemDialog}
          currentUser={{ 
            id: currentUser.id, 
            email: currentUser.email, 
            name: currentUser.name 
          }}
          isConsultant={isConsultant}
          accessToken={accessToken}
          onClose={() => {
            setShowConfigItemDialog(false);
            setSelectedConfigItem(null);
          }}
          onSave={handleSaveConfigItemDetails}
        />
      )}
    </div>
  );
});
